/**
 * /reviews/repos — PR-reviewer repo registry (admin CRUD).
 *
 * Sub-route reached via the "Manage repos" button on /reviews — not a nav
 * item (same pattern as /requests/new off /requests).
 *
 * Permission model (mirrors TeamsPage):
 *   - READ open to any authenticated user.
 *   - WRITE (add repo, toggle enabled/post_to_github, edit the rest) gated
 *     to the admin allowlist (auth/admin.ts — two emails, never hardcoded
 *     here). Backend enforces on every POST/PATCH; the FE additionally
 *     hides write controls for non-admins and shows a read-only Alert.
 *
 * `post_to_github` is the LAUNCH SWITCH: with it off, Sherlock still runs
 * the review and records findings, but nothing is posted to GitHub. Its
 * off-state is made visually explicit (amber "not posting" caption) so an
 * admin never mistakes a silent dry-run repo for a live one.
 *
 * `auto_review_enabled` (0051) controls WHEN reviews run: off (the default)
 * means on-demand only — an author comments "@sherlock review" on the PR;
 * on restores the original auto-on-push behavior. Unlike post_to_github's
 * amber dry-run caption, the off-state here is the normal posture, so it
 * renders in neutral text rather than warning color.
 *
 * `ticket_nudge_enabled` (0058) controls whether the sticky comment asks
 * the author to put a DETECT-#### id in the PR title. Off by default —
 * only repos that actually use the Notion Task/Ticket methodology want
 * this line; for every other repo it's noise on every PR. Same neutral
 * (non-warning) off-state treatment as auto_review_enabled.
 *
 * Known backend limitation (see types/review.ts:ReviewRepoConfigPatch):
 * PATCH can't explicitly null datadog_env/dbm_db_identifier/notes —
 * omitted and null are indistinguishable on the wire. The edit dialog
 * documents "leave blank to clear" and always sends these three fields
 * (as "" when emptied) rather than omitting them, since omitting can
 * never clear a previously-set value.
 */

import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Skeleton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import { useCallback, useEffect, useState, type ReactElement } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { isAdminEmail } from '@/auth/admin';
import { useAuth } from '@/contexts/AuthContext';
import { ApiError, reviews as reviewsApi } from '@/helpers/ApiHelper';
import type {
  ReviewRepoConfigCreate,
  ReviewRepoConfigOut,
  ReviewRepoConfigPatch,
} from '@/types/review';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function splitCsv(value: string): string[] {
  return value
    .split(',')
    .map((v) => v.trim())
    .filter((v) => v.length > 0);
}

const REPO_FULL_NAME_RE = /^[\w.-]+\/[\w.-]+$/;

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function ReviewReposPage(): ReactElement {
  const { user } = useAuth();
  const canEdit = isAdminEmail(user?.email);

  const [configs, setConfigs] = useState<ReviewRepoConfigOut[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rowBusy, setRowBusy] = useState<string | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);

  const [addOpen, setAddOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<ReviewRepoConfigOut | null>(null);

  const load = useCallback(() => {
    setIsLoading(true);
    setError(null);
    reviewsApi.repos
      .list()
      .then((resp) => setConfigs(resp.results))
      .catch((e) => setError(e instanceof ApiError ? e.detail : 'Failed to load repos'))
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleToggle = useCallback(
    async (config: ReviewRepoConfigOut, patch: ReviewRepoConfigPatch) => {
      setRowBusy(config.id);
      setRowError(null);
      try {
        const updated = await reviewsApi.repos.update(config.id, patch);
        setConfigs((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
      } catch (e) {
        setRowError(e instanceof ApiError ? e.detail : 'Update failed');
      } finally {
        setRowBusy(null);
      }
    },
    [],
  );

  if (isLoading) {
    return (
      <Box sx={{ maxWidth: 1100, mx: 'auto', p: 3 }} role="status" aria-label="Loading repo registry">
        <Skeleton variant="text" width={200} height={48} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={280} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ maxWidth: 1100, mx: 'auto', p: 3 }}>
        <Alert severity="error" role="alert" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="outlined" onClick={load}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', p: 3 }}>
      <Box sx={{ mb: 1 }}>
        <Button
          component={RouterLink}
          to="/reviews"
          startIcon={<ArrowBackIcon />}
          size="small"
          sx={{ color: 'text.secondary' }}
        >
          ← reviews
        </Button>
      </Box>

      <Stack direction="row" alignItems="baseline" spacing={2} sx={{ mb: 2 }}>
        <Typography variant="h4" sx={{ fontFamily: '"JetBrains Mono", monospace', color: 'primary.main' }}>
          reviewed repos
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {configs.length} repo{configs.length !== 1 ? 's' : ''} registered
        </Typography>
      </Stack>

      {!canEdit && (
        <Alert severity="info" sx={{ mb: 2 }} role="status">
          Read-only view. Adding repos and changing settings are restricted to admins.
        </Alert>
      )}

      {rowError && (
        <Alert severity="error" role="alert" sx={{ mb: 2 }} onClose={() => setRowError(null)}>
          {rowError}
        </Alert>
      )}

      {configs.length === 0 ? (
        <Box sx={{ py: 6, textAlign: 'center', border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            No repos registered for review yet.
          </Typography>
          {canEdit && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAddOpen(true)} sx={{ minHeight: 44 }}>
              Add repo
            </Button>
          )}
        </Box>
      ) : (
        <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
          <Table size="small" aria-label="Reviewed repos">
            <TableHead>
              <TableRow>
                <TableCell>Repo</TableCell>
                <TableCell>Enabled</TableCell>
                <TableCell>Auto review</TableCell>
                <TableCell>Post to GitHub</TableCell>
                <TableCell>Ticket nudge</TableCell>
                <TableCell>Sentry projects</TableCell>
                <TableCell>Datadog services</TableCell>
                {canEdit && <TableCell align="right">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {configs.map((config) => (
                <TableRow key={config.id} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                      {config.repo_full_name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {canEdit ? (
                      <Switch
                        checked={config.enabled}
                        disabled={rowBusy === config.id}
                        onChange={(e) => void handleToggle(config, { enabled: e.target.checked })}
                        inputProps={{ 'aria-label': `Enabled for ${config.repo_full_name}` }}
                      />
                    ) : (
                      <Chip
                        size="small"
                        label={config.enabled ? 'enabled' : 'disabled'}
                        color={config.enabled ? 'success' : 'default'}
                        variant={config.enabled ? 'filled' : 'outlined'}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Stack spacing={0.25}>
                      {canEdit ? (
                        <Switch
                          checked={config.auto_review_enabled}
                          disabled={rowBusy === config.id}
                          onChange={(e) =>
                            void handleToggle(config, { auto_review_enabled: e.target.checked })
                          }
                          inputProps={{ 'aria-label': `Auto review for ${config.repo_full_name}` }}
                        />
                      ) : (
                        <Chip
                          size="small"
                          label={config.auto_review_enabled ? 'auto' : 'on-demand'}
                          color={config.auto_review_enabled ? 'info' : 'default'}
                          variant={config.auto_review_enabled ? 'filled' : 'outlined'}
                        />
                      )}
                      <Typography
                        variant="caption"
                        sx={{ color: config.auto_review_enabled ? 'info.main' : 'text.secondary' }}
                      >
                        {config.auto_review_enabled
                          ? 'Reviews run on every PR open/push.'
                          : 'On-demand — comment "@sherlock review" on the PR.'}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack spacing={0.25}>
                      {canEdit ? (
                        <Switch
                          checked={config.post_to_github}
                          disabled={rowBusy === config.id}
                          onChange={(e) => void handleToggle(config, { post_to_github: e.target.checked })}
                          inputProps={{ 'aria-label': `Post to GitHub for ${config.repo_full_name}` }}
                        />
                      ) : (
                        <Chip
                          size="small"
                          label={config.post_to_github ? 'posting' : 'not posting'}
                          color={config.post_to_github ? 'success' : 'default'}
                          variant={config.post_to_github ? 'filled' : 'outlined'}
                        />
                      )}
                      {/* Explicit off-state — the launch switch's OFF state must never
                          read as "the same as ON but quieter". */}
                      <Typography
                        variant="caption"
                        sx={{ color: config.post_to_github ? 'success.main' : 'warning.main' }}
                      >
                        {config.post_to_github
                          ? 'Findings are posted to the PR.'
                          : 'Dry-run — findings are recorded but NOT posted.'}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack spacing={0.25}>
                      {canEdit ? (
                        <Switch
                          checked={config.ticket_nudge_enabled}
                          disabled={rowBusy === config.id}
                          onChange={(e) =>
                            void handleToggle(config, { ticket_nudge_enabled: e.target.checked })
                          }
                          inputProps={{ 'aria-label': `Ticket nudge for ${config.repo_full_name}` }}
                        />
                      ) : (
                        <Chip
                          size="small"
                          label={config.ticket_nudge_enabled ? 'nudging' : 'off'}
                          color={config.ticket_nudge_enabled ? 'info' : 'default'}
                          variant={config.ticket_nudge_enabled ? 'filled' : 'outlined'}
                        />
                      )}
                      <Typography
                        variant="caption"
                        sx={{ color: config.ticket_nudge_enabled ? 'info.main' : 'text.secondary' }}
                      >
                        {config.ticket_nudge_enabled
                          ? 'Sticky comment asks for a DETECT id in the title.'
                          : 'No ticket-id nudge.'}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    {config.sentry_project_slugs.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        —
                      </Typography>
                    ) : (
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {config.sentry_project_slugs.map((slug) => (
                          <Chip key={slug} size="small" variant="outlined" label={slug} />
                        ))}
                      </Stack>
                    )}
                  </TableCell>
                  <TableCell>
                    {config.datadog_services.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        —
                      </Typography>
                    ) : (
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {config.datadog_services.map((svc) => (
                          <Chip key={svc} size="small" variant="outlined" label={svc} />
                        ))}
                      </Stack>
                    )}
                  </TableCell>
                  {canEdit && (
                    <TableCell align="right">
                      <Tooltip title="Edit repo settings">
                        <IconButton
                          aria-label={`Edit ${config.repo_full_name}`}
                          size="large"
                          onClick={() => setEditTarget(config)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {canEdit && configs.length > 0 && (
        <Stack direction="row" sx={{ mt: 3 }}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAddOpen(true)} sx={{ minHeight: 44 }}>
            Add repo
          </Button>
        </Stack>
      )}

      {addOpen && (
        <RepoConfigDialog
          mode="create"
          onClose={() => setAddOpen(false)}
          onSaved={(created) => {
            setConfigs((prev) => [...prev, created].sort((a, b) => a.repo_full_name.localeCompare(b.repo_full_name)));
            setAddOpen(false);
          }}
        />
      )}

      {editTarget && (
        <RepoConfigDialog
          mode="edit"
          config={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={(updated) => {
            setConfigs((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
            setEditTarget(null);
          }}
        />
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Create / edit dialog
// ---------------------------------------------------------------------------

interface RepoConfigDialogProps {
  mode: 'create' | 'edit';
  config?: ReviewRepoConfigOut;
  onClose: () => void;
  onSaved: (config: ReviewRepoConfigOut) => void;
}

interface RepoFormValues {
  repo_full_name: string;
  enabled: boolean;
  auto_review_enabled: boolean;
  post_to_github: boolean;
  ticket_nudge_enabled: boolean;
  sentry_project_slugs: string;
  datadog_services: string;
  datadog_env: string;
  dbm_db_identifier: string;
  notes: string;
}

function RepoConfigDialog({ mode, config, onClose, onSaved }: RepoConfigDialogProps): ReactElement {
  const initial: RepoFormValues =
    mode === 'edit' && config
      ? {
          repo_full_name: config.repo_full_name,
          enabled: config.enabled,
          auto_review_enabled: config.auto_review_enabled,
          post_to_github: config.post_to_github,
          ticket_nudge_enabled: config.ticket_nudge_enabled,
          sentry_project_slugs: config.sentry_project_slugs.join(', '),
          datadog_services: config.datadog_services.join(', '),
          datadog_env: config.datadog_env ?? '',
          dbm_db_identifier: config.dbm_db_identifier ?? '',
          notes: config.notes ?? '',
        }
      : {
          repo_full_name: '',
          enabled: true,
          auto_review_enabled: false,
          post_to_github: false,
          ticket_nudge_enabled: false,
          sentry_project_slugs: '',
          datadog_services: '',
          datadog_env: '',
          dbm_db_identifier: '',
          notes: '',
        };

  const [form, setForm] = useState<RepoFormValues>(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = (field: keyof RepoFormValues, value: string | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (): Promise<void> => {
    if (mode === 'create' && !REPO_FULL_NAME_RE.test(form.repo_full_name.trim())) {
      setError('Repo must be in "owner/name" shape.');
      return;
    }

    setBusy(true);
    setError(null);
    try {
      if (mode === 'create') {
        const body: ReviewRepoConfigCreate = {
          repo_full_name: form.repo_full_name.trim(),
          enabled: form.enabled,
          auto_review_enabled: form.auto_review_enabled,
          post_to_github: form.post_to_github,
          ticket_nudge_enabled: form.ticket_nudge_enabled,
          sentry_project_slugs: splitCsv(form.sentry_project_slugs),
          datadog_services: splitCsv(form.datadog_services),
          datadog_env: form.datadog_env.trim() || null,
          dbm_db_identifier: form.dbm_db_identifier.trim() || null,
          notes: form.notes.trim() || null,
        };
        const created = await reviewsApi.repos.create(body);
        onSaved(created);
      } else if (config) {
        // Always send datadog_env/dbm_db_identifier/notes (even "") — the
        // backend can't distinguish omitted from null, so omitting would
        // make it impossible to ever clear a previously-set value.
        const body: ReviewRepoConfigPatch = {
          enabled: form.enabled,
          auto_review_enabled: form.auto_review_enabled,
          post_to_github: form.post_to_github,
          ticket_nudge_enabled: form.ticket_nudge_enabled,
          sentry_project_slugs: splitCsv(form.sentry_project_slugs),
          datadog_services: splitCsv(form.datadog_services),
          datadog_env: form.datadog_env.trim(),
          dbm_db_identifier: form.dbm_db_identifier.trim(),
          notes: form.notes.trim(),
        };
        const updated = await reviewsApi.repos.update(config.id, body);
        onSaved(updated);
      }
    } catch (e) {
      setError(e instanceof ApiError ? e.detail : 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{mode === 'create' ? 'Add repo' : `Edit ${config?.repo_full_name}`}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && (
            <Alert severity="error" role="alert">
              {error}
            </Alert>
          )}

          {mode === 'create' && (
            <TextField
              autoFocus
              label="Repo (owner/name)"
              id="repo-full-name"
              size="small"
              fullWidth
              value={form.repo_full_name}
              onChange={(e) => set('repo_full_name', e.target.value)}
              placeholder="detect-tech/sherlock"
              required
            />
          )}

          <Stack direction="row" spacing={3} alignItems="center" flexWrap="wrap" useFlexGap>
            <Stack direction="row" spacing={1} alignItems="center">
              <Switch
                checked={form.enabled}
                onChange={(e) => set('enabled', e.target.checked)}
                inputProps={{ 'aria-label': 'Enabled' }}
              />
              <Typography variant="body2">Enabled</Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <Switch
                checked={form.auto_review_enabled}
                onChange={(e) => set('auto_review_enabled', e.target.checked)}
                inputProps={{ 'aria-label': 'Auto review on push' }}
              />
              <Typography
                variant="body2"
                sx={{ color: form.auto_review_enabled ? 'info.main' : 'text.secondary' }}
              >
                {form.auto_review_enabled ? 'Auto review on push' : 'On-demand ("@sherlock review")'}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <Switch
                checked={form.post_to_github}
                onChange={(e) => set('post_to_github', e.target.checked)}
                inputProps={{ 'aria-label': 'Post to GitHub' }}
              />
              <Typography variant="body2" sx={{ color: form.post_to_github ? 'success.main' : 'warning.main' }}>
                {form.post_to_github ? 'Post to GitHub' : 'Dry-run (not posting)'}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <Switch
                checked={form.ticket_nudge_enabled}
                onChange={(e) => set('ticket_nudge_enabled', e.target.checked)}
                inputProps={{ 'aria-label': 'Ticket-link nudge' }}
              />
              <Typography
                variant="body2"
                sx={{ color: form.ticket_nudge_enabled ? 'info.main' : 'text.secondary' }}
              >
                {form.ticket_nudge_enabled ? 'Ticket-link nudge on' : 'No ticket-link nudge'}
              </Typography>
            </Stack>
          </Stack>

          <TextField
            label="Sentry project slugs (comma-separated)"
            id="repo-sentry-slugs"
            size="small"
            fullWidth
            value={form.sentry_project_slugs}
            onChange={(e) => set('sentry_project_slugs', e.target.value)}
            placeholder="sherlock-backend, sherlock-frontend"
          />
          <TextField
            label="Datadog services (comma-separated)"
            id="repo-datadog-services"
            size="small"
            fullWidth
            value={form.datadog_services}
            onChange={(e) => set('datadog_services', e.target.value)}
            placeholder="sherlock-api"
          />
          <TextField
            label="Datadog env"
            id="repo-datadog-env"
            size="small"
            fullWidth
            value={form.datadog_env}
            onChange={(e) => set('datadog_env', e.target.value)}
            helperText="Leave blank to clear."
          />
          <TextField
            label="DBM database identifier"
            id="repo-dbm-id"
            size="small"
            fullWidth
            value={form.dbm_db_identifier}
            onChange={(e) => set('dbm_db_identifier', e.target.value)}
            helperText="Leave blank to clear."
          />
          <TextField
            label="Notes"
            id="repo-notes"
            size="small"
            fullWidth
            multiline
            minRows={2}
            value={form.notes}
            onChange={(e) => set('notes', e.target.value)}
            helperText="Leave blank to clear."
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => void handleSubmit()}
          disabled={busy || (mode === 'create' && !form.repo_full_name.trim())}
        >
          {mode === 'create' ? 'Add' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
